import BigNumber from 'bignumber.js';

import { FREQUENCY, FREQUENCY_NUMBER } from '@funnels/constants';
import { formatToPrecision } from '@funnels/utils';

/**
 * Calculate the repayment amount for the balance, interest rate and loan term.
 *
 * @param balance: The balance loan amount
 * @param interestRate: The annual interest rate percentage
 * @param loanTerm: Loan term in months
 * @param interestOnlyPeriod: Default 0. Specify in months. If this is an interest cum balance debt, then the interest only period applicable
 * @param repaymentFrequency: Default Monthly. Repayment frequency. The amount returned corresponds to this frequency
 *
 * @return number: The amount of repayment for the desired frequency
 */
export function calculateRepaymentValue(
  balance?: number,
  interestRate?: number,
  loanTerm?: number,
  interestOnlyPeriod?: number,
  repaymentFrequency?: FREQUENCY
) {
  if (!balance || !interestRate || !loanTerm || !repaymentFrequency) {
    return;
  }
  // prettier-ignore
  const repaymentInterestRate = new BigNumber(interestRate).div(100).div(FREQUENCY_NUMBER[repaymentFrequency]).toNumber();

  if (interestOnlyPeriod) {
    return repaymentInterestOnly(balance, repaymentInterestRate);
  }

  return repaymentBalanceAndInterest(
    balance,
    repaymentInterestRate,
    new BigNumber(loanTerm).div(12).times(FREQUENCY_NUMBER[repaymentFrequency]).toNumber()
  );
}

function repaymentBalanceAndInterest(balance: number, repaymentInterestRate: number, loanTerm: number) {
  const value = new BigNumber(1).plus(repaymentInterestRate).pow(Math.round(loanTerm));

  return formatToPrecision(
    new BigNumber(repaymentInterestRate).times(balance).times(value).div(new BigNumber(value).minus(1)).toNumber()
  );
}

function repaymentInterestOnly(balance: number, repaymentInterestRate: number) {
  return formatToPrecision(new BigNumber(repaymentInterestRate).times(balance).toNumber());
}
